import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import backgroundImage from '../../assets/common/background_homepage.png';
import accessibilityIcon from '../../assets/common/accessablity_icon.png';
import slavexLogoNoText from '../../assets/get_started/logo_without_text.png';
import marketTextImage from '../../assets/auth_choice/market_text.png'
import loginButtonBg from '../../assets/auth_choice/login_button.png';
import signupButtonBg from '../../assets/auth_choice/signup_button.png';
import './AuthChoice.css';

export default function AuthChoice() {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [compactActions, setCompactActions] = useState(false);

  // Ensure our layout fits even when a sticky top navigation exists.
  // We measure it and expose it as a CSS variable used by `AuthChoice.css`.
  useEffect(() => {
    const navEl = document.querySelector('.navigation');
    if (!navEl) return;

    const updateNavHeightVar = () => {
      const height = navEl.getBoundingClientRect().height || 0;
      document.documentElement.style.setProperty('--app-nav-height', `${height}px`);
    };

    updateNavHeightVar();

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateNavHeightVar);
      ro.observe(navEl);
    }

    window.addEventListener('resize', updateNavHeightVar);
    return () => {
      window.removeEventListener('resize', updateNavHeightVar);
      if (ro) ro.disconnect();
    };
  }, []);

  // If MARKET text and the buttons collide on some viewports, shrink the buttons.
  useEffect(() => {
    const layoutEl = document.querySelector('.auth-choice-layout');
    const marketEl = document.querySelector('.auth-choice-market-text');
    const loginBtnEl = document.querySelector('.auth-choice-login-button');
    if (!layoutEl || !marketEl || !loginBtnEl) return;

    const updateCompactActions = () => {
      const marketRect = marketEl.getBoundingClientRect();
      const loginRect = loginBtnEl.getBoundingClientRect();

      // Small buffer so they don't look like they're touching.
      const bufferPx = 12;
      const isColliding = marketRect.bottom + bufferPx >= loginRect.top;
      setCompactActions(isColliding);
    };

    // Run a couple times to account for image/font loading.
    let raf1 = requestAnimationFrame(() => {
      updateCompactActions();
      requestAnimationFrame(updateCompactActions);
    });
    const t1 = window.setTimeout(updateCompactActions, 120);
    const t2 = window.setTimeout(updateCompactActions, 350);

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateCompactActions);
      ro.observe(layoutEl);
    }

    window.addEventListener('resize', updateCompactActions);
    return () => {
      cancelAnimationFrame(raf1);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener('resize', updateCompactActions);
      if (ro) ro.disconnect();
    };
  }, []);

  const handleTermsClick = (e) => {
    e.preventDefault();
    navigate('/terms');
  };

  return (
    <div
      className={`auth-choice-page ${isRTL ? 'rtl' : ''} ${compactActions ? 'compact-actions' : ''}`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="auth-choice-frame" role="main" aria-label="Authentication Choice">
        {/* Background */}
        <div
          className="auth-choice-bg"
          aria-hidden="true"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />

        {/* Accessibility Button (Top Right) */}
        <button
          type="button"
          className="auth-choice-accessibility-button"
          aria-label={t('homepage.accessibility') || 'Accessibility'}
          onClick={() => {
            console.log('Accessibility button clicked');
          }}
        >
          <img
            src={accessibilityIcon}
            alt=""
            className="auth-choice-accessibility-icon"
            aria-hidden="true"
          />
        </button>

        {/* Main centered layout (responsive on iPhone + desktop) */}
        <div className="auth-choice-layout">
          <div className="auth-choice-main">
            {/* Logo Container */}
            <div className="auth-choice-logo-container">
              <img src={slavexLogoNoText} alt={t('app.name')} className="auth-choice-logo-img" />
            </div>

            {/* MARKET Text */}
            <div className="auth-choice-market-text">
              <img src={marketTextImage} alt={t('app.marketText')} className="auth-choice-market-title" />
            </div>
          </div>

          <div className="auth-choice-actions">
            {/* Login Button */}
            <button
              type="button"
              className="auth-choice-login-button"
              onClick={() => navigate('/login')}
            >
              <div className="auth-choice-button-bg">
                <img src={loginButtonBg} alt="" className="auth-choice-button-bg-img" />
              </div>
              <span className="auth-choice-login-text">{t('navigation.login') || 'Login'}</span>
            </button>

            {/* Sign-up Button */}
            <button
              type="button"
              className="auth-choice-signup-button"
              onClick={() => navigate('/signup')}
            >
              <div className="auth-choice-button-bg">
                <img src={signupButtonBg} alt="" className="auth-choice-button-bg-img" />
              </div>
              <span className="auth-choice-signup-text">{t('navigation.signup') || 'Sign-up'}</span>
            </button>

            {/* Terms & Conditions Link */}
            <a
              href="#terms"
              className="auth-choice-terms-link"
              onClick={handleTermsClick}
            >
              <span className="auth-choice-terms-link-text">
                {t('homepage.termsConditions') || 'Terms & Conditions'}
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
